import { useEffect } from 'react';

import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';

import {
  REGION_BORDER,
  REGION_DIM,
  REGION_FILL,
  REGION_HIGHLIGHT,
  REGION_HOVER,
  REGION_LABEL,
  REGION_SOURCE,
} from '../CountryView/constants';
import { Aggregated } from '../CountryView/types';
import { C, ThresholdConfig, getColor } from '../MapSharedData';

// Normalised property name injected during setData. The layer expressions
// reference this stable key, so the layers never need to be re-created when
// the active country swaps a different raw property (Spain uses 'name',
// other configs could vary). Keeps the layers truly country-agnostic.
const REGION_NAME_PROP = '__regionName';

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

interface UseUnifiedRegionLayersOptions {
  mapRef: React.MutableRefObject<MapLibreMap | null>;
  ready: boolean;
  geojson: GeoJSON.FeatureCollection | null;
  /** The raw property name on region features that holds the display name. */
  regionNameProperty: string;
  /** Per-region aggregation result, drives the fill color match expression. */
  aggregated: Aggregated;
  /** Threshold config — low/mid cutoffs for the severity colors. */
  cfg: ThresholdConfig;
  /** Selected region. Null = no region drilled into; dim/highlight stay off. */
  activeRegion: string | null;
  /** True when the active region has loaded postcode polygons. The region's
   *  own fill is then hidden so the polygons render on a neutral background. */
  hasPolygons: boolean;
}

/**
 * Owns the REGION_SOURCE + six region layers on the unified map.
 *
 * Step 2 (REFACTOR_PLAN.md) — sources + layers created hidden so the world
 * view is unaffected. setData fires whenever the active country's geojson
 * resolves; features get a normalised __regionName injected so layer
 * expressions don't need to know the per-country regionNameProperty.
 *
 * Step 3 add-on — the fill-color match expression updates from `aggregated`
 * and `cfg` so once the drill-in hook flips visibility on, regions render in
 * the right severity color immediately.
 *
 * Step 5 add-on — dim/highlight pair tracks `activeRegion`. Mask covers
 * everything except the active region; highlight outlines it. Mirrors the
 * legacy useRegionLayers effect 3 but uses the normalised __regionName.
 */
export function useUnifiedRegionLayers({
  mapRef,
  ready,
  geojson,
  regionNameProperty,
  aggregated,
  cfg,
  activeRegion,
  hasPolygons,
}: UseUnifiedRegionLayersOptions) {
  // 1. Add source + layers once after ready.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (map.getSource(REGION_SOURCE)) return;

    map.addSource(REGION_SOURCE, { type: 'geojson', data: EMPTY_FC, generateId: true });

    map.addLayer({
      id: REGION_FILL,
      type: 'fill',
      source: REGION_SOURCE,
      paint: {
        'fill-color': 'transparent',
        'fill-opacity': 0,
        'fill-outline-color': 'rgba(75,180,179,0.2)',
      },
      layout: { visibility: 'none' },
    });

    map.addLayer({
      id: REGION_BORDER,
      type: 'line',
      source: REGION_SOURCE,
      paint: { 'line-color': 'rgba(75,180,179,0.3)', 'line-width': 1, 'line-opacity': 0 },
      layout: { visibility: 'none' },
    });

    map.addLayer({
      id: REGION_HOVER,
      type: 'fill',
      source: REGION_SOURCE,
      paint: {
        'fill-color': C.primary,
        'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.25, 0],
      },
      layout: { visibility: 'none' },
    });

    map.addLayer({
      id: REGION_LABEL,
      type: 'symbol',
      source: REGION_SOURCE,
      minzoom: 3,
      layout: {
        'text-field': ['get', REGION_NAME_PROP],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 3, 9, 5, 11, 8, 14],
        'text-anchor': 'center',
        'text-allow-overlap': false,
        'text-padding': 2,
        visibility: 'none',
      },
      paint: {
        'text-color': C.textPrimary,
        'text-halo-color': 'rgba(255,255,255,0.95)',
        'text-halo-width': 1.6,
        'text-halo-blur': 0.5,
        'text-opacity': ['interpolate', ['linear'], ['zoom'], 3, 0, 3.5, 1],
      },
    });

    map.addLayer({
      id: REGION_DIM,
      type: 'fill',
      source: REGION_SOURCE,
      filter: ['!=', ['get', REGION_NAME_PROP], '__NONE__'],
      paint: { 'fill-color': 'rgba(0,0,0,0.45)', 'fill-opacity': 0 },
      layout: { visibility: 'none' },
    });

    map.addLayer({
      id: REGION_HIGHLIGHT,
      type: 'fill',
      source: REGION_SOURCE,
      filter: ['==', ['get', REGION_NAME_PROP], '__NONE__'],
      paint: { 'fill-color': C.primary, 'fill-opacity': 0 },
      layout: { visibility: 'none' },
    });
  }, [mapRef, ready]);

  // 2. setData when geojson arrives. Inject __regionName from the country's
  //    regionNameProperty so the layer expressions stay country-agnostic.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const source = map.getSource(REGION_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    if (!geojson) {
      source.setData(EMPTY_FC);
      return;
    }

    const features: GeoJSON.Feature[] = geojson.features.map((f) => ({
      ...f,
      properties: {
        ...(f.properties ?? {}),
        [REGION_NAME_PROP]: f.properties?.[regionNameProperty] ?? '',
      },
    }));

    source.setData({ type: 'FeatureCollection', features });
  }, [mapRef, ready, geojson, regionNameProperty]);

  // 3. Step-3 add-on — update REGION_FILL color when aggregated/cfg change.
  //    Match expression: __regionName → severity color. The layer references
  //    __regionName (not the raw regionNameProperty) for the same reason as
  //    above, but the aggregated keys ARE the values of regionNameProperty,
  //    so they line up.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!map.getLayer(REGION_FILL)) return;

    const pairs: (string | maplibregl.ExpressionSpecification)[] = [];
    for (const [name, data] of Object.entries(aggregated)) {
      pairs.push(name, getColor(data.total, cfg));
    }
    const expr: maplibregl.ExpressionSpecification = pairs.length
      ? (['match', ['get', REGION_NAME_PROP], ...pairs, 'rgba(0,0,0,0)'] as any)
      : ('rgba(0,0,0,0)' as any);

    map.setPaintProperty(REGION_FILL, 'fill-color', expr);
  }, [mapRef, ready, aggregated, cfg]);

  // 4. Step-5 add-on — dim/highlight pair when a region is selected. Mirrors
  //    the legacy useRegionLayers effect 3 but reads the normalised
  //    __regionName so it works without re-creating layers per country.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!map.getLayer(REGION_DIM) || !map.getLayer(REGION_HIGHLIGHT)) return;

    if (activeRegion) {
      map.setFilter(REGION_DIM, ['!=', ['get', REGION_NAME_PROP], activeRegion]);
      map.setFilter(REGION_HIGHLIGHT, ['==', ['get', REGION_NAME_PROP], activeRegion]);
      map.setLayoutProperty(REGION_DIM, 'visibility', 'visible');
      map.setLayoutProperty(REGION_HIGHLIGHT, 'visibility', 'visible');
      map.setPaintProperty(REGION_DIM, 'fill-opacity', 0.55);
      map.setPaintProperty(REGION_HIGHLIGHT, 'fill-opacity', 0.08);

      if (hasPolygons) {
        // Hide the active region's own color so the postcode polygons render
        // on a neutral background — red polygons over a red region were
        // unreadable in the legacy implementation.
        map.setPaintProperty(REGION_FILL, 'fill-opacity', [
          'case',
          ['==', ['get', REGION_NAME_PROP], activeRegion],
          0,
          0.75,
        ] as any);
      } else {
        // Dots-only mode — keep the region coloured so severity context
        // stays visible behind the markers.
        map.setPaintProperty(REGION_FILL, 'fill-opacity', 0.75);
      }
    } else {
      // No region selected — fade dim/highlight to 0 (transition handles
      // the smoothness if set by drill-in). Reset REGION_FILL to the flat
      // 0.75 the country overview expects.
      map.setFilter(REGION_HIGHLIGHT, ['==', ['get', REGION_NAME_PROP], '__NONE__']);
      map.setPaintProperty(REGION_DIM, 'fill-opacity', 0);
      map.setPaintProperty(REGION_HIGHLIGHT, 'fill-opacity', 0);
      map.setPaintProperty(REGION_FILL, 'fill-opacity', 0.75);
    }
  }, [mapRef, ready, activeRegion, hasPolygons]);
}
